// api/musicService.js
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL;

// Configure axios defaults
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add request interceptor for auth token if needed
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Handle unauthorized access
      localStorage.removeItem('token');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Artists Service
export const artistService = {
  getAllArtists: () => apiClient.get('/artists'),
  getArtistById: (id) => apiClient.get(`/artists/${id}`),
  createArtist: (artistData) => apiClient.post('/artists', artistData),
  updateArtist: (id, artistData) => apiClient.put(`/artists/${id}`, artistData),
  deleteArtist: (id) => apiClient.delete(`/artists/${id}`),
  searchArtists: (query) => apiClient.get(`/artists/search?q=${query}`),
};

// Albums Service
export const albumService = {
  getAllAlbums: (params) => apiClient.get('/albums', { params }),
  getAlbumById: (id) => apiClient.get(`/albums/${id}`),
  getAlbumsByArtist: (artistId) => apiClient.get(`/albums/artist/${artistId}`),
  createAlbum: (albumData) => apiClient.post('/albums', albumData),
  updateAlbum: (id, albumData) => apiClient.put(`/albums/${id}`, albumData),
  deleteAlbum: (id) => apiClient.delete(`/albums/${id}`),
  uploadCover: (id, formData) => apiClient.post(`/albums/${id}/cover`, formData),
};

// Songs Service
export const songService = {
  getAllSongs: (params) => apiClient.get('/songs', { params }),
  getSongById: (id) => apiClient.get(`/songs/${id}`),
  createSong: (songData) => apiClient.post('/songs', songData),
  updateSong: (id, songData) => apiClient.put(`/songs/${id}`, songData),
  deleteSong: (id) => apiClient.delete(`/songs/${id}`),
  incrementPlayCount: (id) => apiClient.post(`/songs/${id}/play`),
  searchSongs: (query) => apiClient.get(`/songs/search?q=${query}`),
  uploadAudio: (formData, onUploadProgress) => 
    apiClient.post('/songs/upload/audio', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress,
    }),
  addGenreToSong: (songId, genreId) => apiClient.post(`/songs/${songId}/genres/${genreId}`),
  removeGenreFromSong: (songId, genreId) => apiClient.delete(`/songs/${songId}/genres/${genreId}`),
};

// Playlists Service
export const playlistService = {
  getAllPlaylists: (params) => apiClient.get('/playlists', { params }),
  getPlaylistById: (id) => apiClient.get(`/playlists/${id}`),
  createPlaylist: (playlistData) => apiClient.post('/playlists', playlistData),
  updatePlaylist: (id, playlistData) => apiClient.put(`/playlists/${id}`, playlistData),
  deletePlaylist: (id) => apiClient.delete(`/playlists/${id}`),
  addSongToPlaylist: (playlistId, songId, position) => 
    apiClient.post(`/playlists/${playlistId}/songs/${songId}`, { position }),
  removeSongFromPlaylist: (playlistId, songId) => 
    apiClient.delete(`/playlists/${playlistId}/songs/${songId}`),
  updateSongPosition: (playlistId, songId, newPosition) => 
    apiClient.put(`/playlists/${playlistId}/songs/${songId}/position`, { newPosition }),
  getUserPlaylists: (userId) => apiClient.get(`/playlists/user/${userId}`),
};

// Genres Service
export const genreService = {
  getAllGenres: () => apiClient.get('/genres'),
  getGenreById: (id) => apiClient.get(`/genres/${id}`),
  createGenre: (genreData) => apiClient.post('/genres', genreData),
  updateGenre: (id, genreData) => apiClient.put(`/genres/${id}`, genreData),
  deleteGenre: (id) => apiClient.delete(`/genres/${id}`),
  getSongsByGenre: (genreId) => apiClient.get(`/genres/${genreId}/songs`),
};

// Music Analytics Service
export const analyticsService = {
  getTopSongs: (limit = 10) => apiClient.get(`/analytics/top-songs?limit=${limit}`),
  getTopArtists: (limit = 10) => apiClient.get(`/analytics/top-artists?limit=${limit}`),
  getMostPlayedGenres: (limit = 5) => apiClient.get(`/analytics/top-genres?limit=${limit}`),
  getMonthlyStats: () => apiClient.get('/analytics/monthly-stats'),
  getUserActivity: (userId) => apiClient.get(`/analytics/user/${userId}/activity`),
};

// Bulk Operations
export const bulkService = {
  importSongs: (songsData) => apiClient.post('/bulk/import/songs', songsData),
  importArtists: (artistsData) => apiClient.post('/bulk/import/artists', artistsData),
  exportData: (type) => apiClient.get(`/bulk/export/${type}`, { responseType: 'blob' }),
  deleteMultiple: (type, ids) => apiClient.post(`/bulk/delete/${type}`, { ids }),
};

// Music Admin Statistics
export const adminStatsService = {
  getDashboardStats: () => apiClient.get('/admin/stats'),
  getRecentActivity: () => apiClient.get('/admin/activity'),
  getStorageUsage: () => apiClient.get('/admin/storage'),
};

export default {
  artistService,
  albumService,
  songService,
  playlistService,
  genreService,
  analyticsService,
  bulkService,
  adminStatsService,
};